import { appConfirm } from './confirm-dialog.js';
import { openPeoplePicker, peopleIndex, personEl } from './people-picker.js';
import { navigate } from './calendar-spa.js';
import { setupWorkPanel } from './work-panel.js';

// 업무 요청 작성·상세 (다섯 모듈 UX §5.3·§5.4). 문서 리스너는 한 번만 걸고 root 는
// 그때그때 #workMain 을 찾는다 — 등록 뒤 상세로 가는 이동이 SPA navigate() 라
// calendar:page-swapped 가 main 을 통째로 갈아끼우고, 액션 뒤 제자리 갱신도
// #workMain 을 바꾼다. 닫힌 변수로 잡아 두면 죽은 노드를 만진다.
const state = { busy: false, navigating: false, needsReload: false, baseline: new WeakMap() };
const root = () => document.getElementById('workMain');
const labels = () => { try { return JSON.parse(document.getElementById('workLabels')?.textContent || '{}'); } catch (_) { return {}; } };
const forms = () => [...(root()?.querySelectorAll('[data-work-form]') || [])];
const snapshot = form => [...new FormData(form)].filter(([name]) => !['if_version', '_csrf'].includes(name));
const serialized = form => JSON.stringify(snapshot(form));
const dirty = form => state.baseline.has(form) && state.baseline.get(form) !== serialized(form);
const CSRF = () => document.querySelector('meta[name=csrf-token]')?.content || '';

function feedback(message, error = false) {
    const el = root()?.querySelector('#workFeedback');
    if (!el) return;
    el.hidden = false; el.classList.toggle('error', error); el.textContent = message; el.focus();
}

// hr-person-field 파셜(버튼 + 숨은 input)에 people picker 를 건다. hr-operations.js 의
// 바인더와 같은 계약: data-allow 밖은 exclude, data-required 가 없으면 「없음」 허용.
function bindPeople(scope, L) {
    scope.querySelectorAll('[data-hr-person]').forEach(field => {
        if (field.dataset.pickerReady) return;
        field.dataset.pickerReady = '1';
        const name = field.dataset.hrPerson, required = field.dataset.required === '1';
        const allow = field.dataset.allow ? new Set(field.dataset.allow.split(',').filter(Boolean)) : null;
        const button = field.querySelector('.hr-person-btn'), view = field.querySelector('.hr-person-view');
        if (!button || !view) return;
        const values = () => [...field.querySelectorAll('[data-hr-person-value]')].map(i => i.value).filter(Boolean);
        const write = ids => {
            field.querySelectorAll('[data-hr-person-value]').forEach(i => i.remove());
            for (const id of (ids.length ? ids : [''])) {
                const input = document.createElement('input');
                input.type = 'hidden'; input.name = name; input.value = id; input.dataset.hrPersonValue = '';
                field.append(input);
            }
            const idx = peopleIndex();
            view.replaceChildren(...ids.map(id => idx.byId[id] ? personEl(idx.byId[id], 'xs') : Object.assign(document.createElement('span'), { textContent: id })));
            if (!ids.length) { const empty = document.createElement('span'); empty.className = 'text-muted'; empty.textContent = L.pick || ''; view.append(empty); }
            field.dispatchEvent(new Event('change', { bubbles: true }));
        };
        button.addEventListener('click', async () => {
            button.disabled = true;
            try {
                const index = peopleIndex(); await index.ready;
                // 승인자 칸에는 「나에게 배정」 지름길이 없다 — 요청자는 승인자가 될 수 없다.
                const opts = { values: values(), multi: false, allowNone: !required, allowMe: name !== 'approver_id', onChange: write };
                if (allow) opts.exclude = index.list.filter(p => !allow.has(p.id)).map(p => p.id);
                openPeoplePicker(button, opts);
            } finally { button.disabled = false; }
        });
    });
}

// 담당자 = 나 자신은 경고 문장만(막지 않는다), 승인자 = 담당자는 제출 전에 막는다 (§5.3).
function checkPeople(form, L) {
    const warn = form.querySelector('[data-work-warn]');
    if (!warn) return true;
    const value = name => { const f = form.elements.namedItem(name); return f && 'value' in f ? f.value : ''; };
    const me = form.dataset.me || '', assignee = value('assignee_id'), approver = value('approver_id');
    const notes = [];
    let ok = true;
    if (assignee && me && assignee === me) notes.push(L.selfAssign);
    // 승인자 = 담당자, 승인자 = 나(요청자) 둘 다 제출 전에 막는다 (서버도 work.self_approval 400).
    if (approver && ((assignee && approver === assignee) || (me && approver === me))) { notes.push(L.selfApproval); ok = false; }
    warn.textContent = notes.filter(Boolean).join(' ');
    warn.hidden = !notes.length;
    warn.classList.toggle('error', !ok);
    return ok;
}

function bindPage() {
    const r = root();
    if (!r) return;
    r.dataset.ready = '1';
    const L = labels();
    forms().forEach(form => { if (!state.baseline.has(form)) state.baseline.set(form, serialized(form)); });
    bindPeople(r, L);
    forms().forEach(form => { if (form.dataset.me) checkPeople(form, L); });
}

// 액션 뒤 #workMain 만 갈아끼우되 스크롤·포커스를 유지한다 (§5.4). 스크롤 컨테이너는
// main 자신일 수도, 창일 수도 있어 둘 다 기억한다.
function swapMain(replacement) {
    const old = root();
    const winY = window.scrollY, mainY = old.scrollTop;
    const active = document.activeElement;
    const key = active && old.contains(active) ? { form: active.closest('[data-work-form]')?.dataset.workKey, name: active.name, id: active.id } : null;
    old.replaceWith(replacement);
    replacement.dataset.ready = '1';
    bindPage();
    replacement.scrollTop = mainY;
    window.scrollTo(0, winY);
    if (key) {
        const again = (key.id && replacement.querySelector('#' + CSS.escape(key.id)))
            || (key.form && key.name && replacement.querySelector('[data-work-key="' + CSS.escape(key.form) + '"] [name="' + CSS.escape(key.name) + '"]'));
        if (again && again.focus) again.focus({ preventScroll: true });
    }
}

async function onSubmit(event) {
    const form = event.target;
    const r = root();
    // 패널 안의 폼은 work-panel.js 가 맡는다 (그쪽이 캡처 단계에서 멈추지만, 등록 순서가 바뀌어도 안전하도록).
    if (!r || !r.contains(form) || !form.matches('[data-work-form]') || form.closest('#workPanelBody')) return;
    event.preventDefault();
    if (state.busy || state.needsReload) return;
    const L = labels();
    if (!checkPeople(form, L)) { feedback(L.selfApproval || L.failed, true); return; }
    const action = event.submitter?.value || form.elements.action?.value;
    const others = forms().filter(other => other !== form && dirty(other));
    if (others.length && ['submit', 'approve', 'reject', 'cancel', 'reopen'].includes(action)) { feedback(L.saveOther, true); return; }
    state.busy = true;
    const sent = serialized(form);
    const body = new FormData(form);
    if (event.submitter?.name) body.set(event.submitter.name, event.submitter.value);
    const buttons = [...r.querySelectorAll('button[type=submit],button:not([type])')];
    const wasDisabled = new Map(buttons.map(button => [button, button.disabled]));
    buttons.forEach(button => { button.disabled = true; });
    let committed = false;
    try {
        const response = await fetch(form.getAttribute('action'), { method: 'POST', body, headers: { Accept: 'application/json', 'X-CSRF-Token': CSRF() } });
        const data = await response.json();
        if (!response.ok) throw Error(typeof data.message === 'string' ? data.message : L.failed);
        committed = true;
        // 등록 뒤 상세로 가는 이동은 SPA 다 (§5.3) — 실패하면 navigate 가 진짜 이동으로 떨어진다.
        if (data.url) { state.navigating = true; navigate(data.url); return; }
        r.querySelectorAll('[name=if_version]').forEach(input => { input.value = String(data.version); });
        const fresh = await fetch(location.href, { headers: { Accept: 'text/html' } });
        if (!fresh.ok) throw Error(L.refreshFailed);
        const doc = new DOMParser().parseFromString(await fresh.text(), 'text/html');
        const replacement = doc.getElementById('workMain');
        if (!replacement) throw Error(L.refreshFailed);
        const preserved = new Map();
        forms().forEach(old => { if (old === form ? serialized(old) !== sent : dirty(old)) preserved.set(old.dataset.workKey, snapshot(old)); });
        const openEditors = [...r.querySelectorAll('details[open]:has([data-work-editor])')].map(el => el.querySelector('[data-work-editor]').dataset.workKey);
        swapMain(replacement);
        forms().forEach(next => {
            const values = preserved.get(next.dataset.workKey);
            if (values) for (const [name, value] of values) { const field = next.elements.namedItem(name); if (field && 'value' in field) field.value = value; }
            if (openEditors.includes(next.dataset.workKey)) next.closest('details').open = true;
        });
        feedback(data.message || L.saved);
    } catch (error) {
        feedback(committed ? L.refreshFailed : error.message || L.failed, true);
        if (committed) {
            state.needsReload = true;
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'btn btn-secondary'; button.textContent = L.reload;
            button.addEventListener('click', async () => { if (!forms().some(dirty) || await appConfirm({ message: L.unsaved })) { state.navigating = true; navigate(location.href, { replace: true, swap: true }); } });
            root()?.querySelector('#workFeedback')?.append(button);
        }
    } finally {
        state.busy = false;
        buttons.forEach(button => { if (button.isConnected) button.disabled = state.needsReload || wasDisabled.get(button); });
    }
}

export function setupWork() {
    // §11 상세 패널은 목록 화면의 것이지만 문서 리스너는 어느 화면에서나 건다 — SPA 로
    // 목록에 도착했을 때(#workMain 이 없던 화면에서 출발) 살아 있어야 한다.
    setupWorkPanel();
    if (!root()) return;
    bindPage();
    if (document.__workBound) return;
    document.__workBound = true;
    // 등록 → 상세의 SPA 교체 뒤 새 main 에 다시 붙는다.
    document.addEventListener('calendar:page-swapped', () => { state.navigating = false; state.needsReload = false; bindPage(); });
    window.addEventListener('beforeunload', event => { if (!state.navigating && forms().some(dirty)) { event.preventDefault(); event.returnValue = ''; } });
    document.addEventListener('click', async event => {
        const link = event.target.closest('a[href]');
        if (!link || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download') || link.getAttribute('href').startsWith('#') || !forms().some(dirty)) return;
        event.preventDefault();
        if (await appConfirm({ message: labels().unsaved })) { state.navigating = true; location.assign(link.href); }
    });
    document.addEventListener('click', event => {
        const chip = event.target.closest('.work-due-preset');
        if (!chip) return;
        const input = chip.closest('form')?.elements.namedItem('due_date');
        if (!input) return;
        input.value = chip.dataset.due || '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    document.addEventListener('change', event => {
        const form = event.target.closest('[data-work-form][data-me]');
        if (form) checkPeople(form, labels());
    });
    document.addEventListener('keydown', event => {
        if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return;
        const r = root();
        const form = document.activeElement?.closest('[data-work-editor]') || r?.querySelector('[data-work-key=create]') || r?.querySelector('details[open] [data-work-editor]');
        if (form) { event.preventDefault(); form.requestSubmit(); }
    });
    document.addEventListener('submit', onSubmit);
}
